import MuiButton from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import { ComponentProps } from 'react';
import { LoadingSpinnerIcon } from '../theme/styled-components';

const useStyles = makeStyles((theme) => ({
  root: {
    '& .MuiButton-label': {
      display: 'flex',
      alignItems: 'center',
      gap: 16,
    },
  },
  primary: {
    backgroundColor: theme.palette.common.coral,
    fontWeight: 400,
    margin: 0,
    padding: '5px 30px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: theme.palette.common.midBlue,
      border: `2px solid ${theme.palette.common.coral}`,
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.action
        ? theme.palette.action.disabledBackground
        : theme.palette.common.backgroundMuted,
      color: theme.palette.action
        ? theme.palette.action.disabled
        : theme.palette.common.backgroundMuted,
      border: '2px solid transparent',
      cursor: 'not-allowed',
      boxShadow: 'none',
      pointerEvents: 'none',
    },
  },
  secondary: {
    fontWeight: 400,
    padding: '5px 30px',
    margin: 0,
    textTransform: 'none',
    '&.Mui-disabled': {
      borderWidth: 2,
    },
    '& .MuiButton-label': {
      gap: 16,
    },
  },
  tertiary: {
    backgroundColor:
      theme.palette.type === 'light'
        ? theme.palette.common.white
        : theme.palette.common.backgroundDark,
    color:
      theme.palette.type === 'light' ? theme.palette.common.midBlue : undefined,
    borderRadius: 10,
    fontSize: 16,
    fontWeight: 600,
    padding: '24px 32px',
    margin: 0,
    textTransform: 'none',
    '& .MuiButton-label': {
      gap: 16,
    },
  },
  text: {
    textTransform: 'none',
  },
}));

type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'text';

interface ButtonProps
  extends Omit<ComponentProps<typeof MuiButton>, 'variant'> {
  /** Button loading state */
  loading?: boolean;

  /** Choose between `primary`, `secondary` and `text`. Defaults to `primary`. */
  variant?: ButtonVariant;
}

function Button({
  children,
  disabled,
  loading = false,
  variant = 'primary',
  ...rest
}: ButtonProps) {
  const classes = useStyles();

  const buttonVariant = variant === 'text' ? 'text' : 'outlined';

  return (
    <MuiButton
      className={[classes.root, classes[variant]].join(' ')}
      disabled={disabled || loading}
      variant={buttonVariant}
      {...rest}
    >
      {children}
      {loading && <LoadingSpinnerIcon />}
    </MuiButton>
  );
}

export default Button;
